import { useState, type ReactNode } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { cn } from '@/utils/cn';
import { Card } from '../Card';
import type { CardProps } from '../Card';
import { Icon } from '../Icon';
import { Typography } from '../Typography';

// ─── Types ────────────────────────────────────────────────────────────────────

/** A single item in an `AccordionList`. */
export interface AccordionListItem {
  /** The row header text. Rendered as `header/h3`. */
  title: string;
  /**
   * The body content revealed when the item is open.
   * Accepts `ReactNode` so callers can include links, emphasis, etc.
   * Wrap plain text in `<Typography variant="body" size="small" color="bodylight">`.
   */
  content: ReactNode;
  /**
   * Whether this item starts in the open state.
   *
   * @default false
   */
  defaultOpen?: boolean;
}

/**
 * Props for the AccordionList component.
 *
 * `className` is intentionally omitted — AccordionList is a sealed design
 * system component. If a new visual state is needed, add a named prop.
 */
export interface AccordionListProps {
  /** Items to render. Each item manages its own open/closed state. */
  items: AccordionListItem[];
  /**
   * Shadow depth forwarded to the underlying `Card`.
   * Omit for a flat card with no drop shadow.
   */
  shadow?: CardProps['shadow'];
}

// ─── AccordionRow ─────────────────────────────────────────────────────────────

interface AccordionRowProps extends AccordionListItem {
  isLast: boolean;
}

function AccordionRow({ title, content, defaultOpen = false, isLast }: AccordionRowProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className={isLast ? undefined : 'border-b border-default'}>
      {/* Header button */}
      <button
        type="button"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((prev) => !prev)}
        className="flex items-center justify-between w-full p-4 text-left cursor-pointer"
      >
        <Typography variant="header" size="h3">
          {title}
        </Typography>
        <span
          className={cn(
            'flex-shrink-0 text-header transition-transform duration-200',
            isOpen && 'rotate-180'
          )}
          aria-hidden="true"
        >
          <Icon name="chevron-down" size="large" />
        </span>
      </button>

      {/* Animated content panel */}
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4">{content}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── AccordionList ────────────────────────────────────────────────────────────

/**
 * Primer AccordionList component.
 *
 * A sealed card-wrapped list of independently expandable rows. Each row shows
 * a title and an animated chevron; clicking reveals the body content with a
 * smooth height animation (Framer Motion).
 *
 * Replaces the legacy `src/components/ui/Accordion` component.
 *
 * ### Sealed component
 * `className` is not exposed. Chrome (card border, radius, dividers) is fixed.
 *
 * ### Data-driven
 * Pass items via the `items` prop. Each item controls its own open/closed state.
 *
 * @example
 * <AccordionList
 *   items={[
 *     {
 *       title: 'What is Influenster for Creators?',
 *       content: (
 *         <Typography variant="body" size="small" color="bodylight">
 *           Influenster for Creators connects content creators with brands.
 *         </Typography>
 *       ),
 *     },
 *   ]}
 * />
 */
export function AccordionList({ items, shadow }: AccordionListProps) {
  return (
    <Card shadow={shadow}>
      {items.map((item, index) => (
        <AccordionRow key={index} {...item} isLast={index === items.length - 1} />
      ))}
    </Card>
  );
}

export default AccordionList;
